import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import FakeLink from "@/components/FakeLink/FakeLink";
import HelpButton from "@/components/HelpButton/HelpButton";
import ImpactPill from "@/components/Impact/ImpactPill";
import MoreButton from "@/components/MoreButton/MoreButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import UnrelatedTag from "@/components/StatusTags/UnrelatedTag";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import useAnalytics from "@/hooks/useAnalytics";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { IImpact } from "@/pages/dashboard/utils/types";
import {
  ApiOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DeleteMaterialAction from "../DeleteMaterialAction";
import ChangeImpactReferenceAction from "../MaterialImpactModal/ChangeImpactReferenceAction";
import { useGetMaterials, useMaterialStatusPerYear } from "../useMaterials";
import styles from "./MaterialsOverview.module.scss";

export interface Material {
  id: number;
  external_id: string;
  name: string;
  supplier_name: string | null;
  supplier_address: string;
  status: "complete" | "incomplete" | "out_of_scope";
  match?: {
    id: number;
    name: string;
    impact?: IImpact;
  };
  relations: number;
  unrelated: boolean;
}

export default function MaterialsOverview() {
  useTrackOnLoad("Materials Viewed");

  const [selectedMaterials, setSelectedMaterials] = useState<Material[]>([]);
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { year } = useDashboardYear();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const deselectUndeletableMaterials = () => {
    setSelectedMaterials((selectedMaterials) => [
      ...selectedMaterials.filter((x) => x.relations === 0),
    ]);
  };

  const columns: Column<Material>[] = [
    {
      key: "name",
      label: "Material",
      sortable: true,
      searchable: true,
      minWidth: "160px",
    },
    {
      key: "supplier_name",
      label: "Supplier",
      sortable: true,
      searchable: true,
      minWidth: "160px",
    },
    {
      key: "supplier_address",
      label: "Supplier address",
      sortable: true,
      searchable: true,
      minWidth: "220px",
    },
    {
      key: "dataset_name",
      label: "Impact match",
      sortable: true,
      searchable: true,
      minWidth: "190px",
      renderer: (record) => (record.match ? record.match.name : "-"),
    },
    {
      key: "impact_match",
      label: "Impact",
      maxWidth: "250px",
      minWidth: "250px",
      renderer: (record) => (
        <ImpactPill
          impact={record.match?.impact}
          incomplete={!record.match}
          incompleteTooltip={"Material does not have an impact reference assigned."}
        />
      ),
    },
    {
      key: "relations",
      label: "Relations",
      maxWidth: "150px",
      minWidth: "150px",
      sortable: true,
      renderer: (record) => (
        <>
          <span>{record.relations} products</span>
          {record.unrelated && (
            <Tooltip title="This material is not related to purchase or sales orders via a product, so it will be excluded from impact calculation">
              <>
                <UnrelatedTag />
              </>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      key: "status",
      label: "Status",
      maxWidth: "150px",
      minWidth: "150px",
      sortable: true,
      renderer: (product) => (
        <StatusTag
          status={product.status}
          showIcon={false}
        />
      ),
      filterable: true,
    },
    {
      key: "actions",
      label: "Actions",
      minWidth: "80px",
      maxWidth: "80px",
      noLink: true,
      renderer: (material) => (
        <ActionsPopover
          actions={
            <>
              <ActionsPopOverItem
                onClick={() => {
                  navigate(`${material.id}`);
                }}
                icon={<EditOutlined />}
              >
                Material details
              </ActionsPopOverItem>
              <ChangeImpactReferenceAction
                state="edit"
                materialIds={[material.id]}
              >
                <ActionsPopOverItem icon={<ApiOutlined />}>
                  Edit impact reference
                </ActionsPopOverItem>
              </ChangeImpactReferenceAction>
              <DeleteMaterialAction
                deselectUndeletableProducts={deselectUndeletableMaterials}
                onSucces={() => {
                  setSelectedMaterials([]);
                  analytics.track("Material Deleted");
                }}
                materials={[material]}
              >
                <ActionsPopOverItem
                  icon={<DeleteOutlined />}
                  disabled={material.relations > 0}
                  danger
                >
                  Delete material
                </ActionsPopOverItem>
              </DeleteMaterialAction>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
      align: "end",
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "relations", order: "descend" },
    });
  const { data, isLoading, isFetching } = useGetMaterials({
    year,
    showUnrelated,
    filter,
    sort,
    search,
    pagination,
  });

  const onSelectedKeysChange = (keys: number[]) => {
    analytics.track("Material Selected");

    setSelectedMaterials((currentMaterials) => {
      let productsOnOtherPages = [
        ...currentMaterials.filter((x) => !data?.data.map((x) => x.id).includes(x.id)),
      ];
      return [
        ...productsOnOtherPages,
        ...(data?.data.filter((material) => keys.includes(material.id)) || []),
      ];
    });
  };

  const resetTable = () => {
    setPage(1);
    setSelectedMaterials([]);
  };

  const { data: materialsYears } = useMaterialStatusPerYear();

  return (
    <div className={styles.ProductsOverview}>
      <PageHeader
        title="Materials"
        description="Your materials database built from materials detected in your product's BOMs. Manage material names and assign an impact reference to each material."
        right={
          <>
            <HelpButton
              type="text"
              url="https://help.root-sustainability.com/en/articles/9898702-materials"
            />
            <MoreButton
              actions={
                <ActionsPopOverItem
                  to="/impact_references"
                  icon={<SettingOutlined />}
                >
                  Manage impact references
                </ActionsPopOverItem>
              }
            />
          </>
        }
      />
      <div>
        <TableHeader
          style={{ height: 64 }}
          left={
            <>
              {selectedMaterials.length > 0 ? (
                <span className={styles["num-selected"]}>{selectedMaterials.length} selected:</span>
              ) : (
                <YearSelector
                  disableYears={showUnrelated}
                  optionData={materialsYears}
                  onChange={resetTable}
                  right={
                    <Tooltip
                      placement="bottom"
                      title="Show materials that are not related to purchase or sales orders"
                    >
                      <>
                        <SwitchWithLabel
                          togglePosition="right"
                          size="small"
                          checked={showUnrelated}
                          onChange={(disabled) => {
                            resetTable();
                            setShowUnrelated(disabled);
                          }}
                        >
                          Year unrelated
                        </SwitchWithLabel>
                      </>
                    </Tooltip>
                  }
                />
              )}
              <PageButtonGroup loading={isLoading}>
                {selectedMaterials.length > 0 ? (
                  <>
                    <ChangeImpactReferenceAction
                      state="edit"
                      materialIds={selectedMaterials.map((x) => x.id)}
                    >
                      <Button icon={<EditOutlined />}>Add/update impact reference</Button>
                    </ChangeImpactReferenceAction>
                    <DeleteMaterialAction
                      deselectUndeletableProducts={deselectUndeletableMaterials}
                      materials={selectedMaterials}
                      onSucces={() => {
                        setSelectedMaterials([]);
                        analytics.track("Materials Deleted");
                      }}
                    >
                      <Button
                        icon={<DeleteOutlined style={{ color: "#f5222d" }} />}
                        disabled={selectedMaterials.some((x) => x.relations > 0)}
                      />
                    </DeleteMaterialAction>
                  </>
                ) : (
                  <></>
                )}
              </PageButtonGroup>
              {selectedMaterials.length > 0 && (
                <FakeLink onClick={() => setSelectedMaterials([])}>Clear selection</FakeLink>
              )}
            </>
          }
          right={
            <StatusTags
              data={data}
              loading={isLoading}
            />
          }
        />
        <Table<Material>
          availableFilters={{
            status: [
              { label: "Complete", value: "complete" },
              { label: "Incomplete", value: "incomplete" },
              { label: "Out of scope", value: "out_of_scope" },
            ],
            relation: [
              { label: "Source", value: "primary_source" },
              { label: "Proxy", value: "proxy" },
            ],
          }}
          loading={isLoading || isFetching}
          rowKey="id"
          checkable={true}
          data={data?.data}
          onSearchFilterSortPaginate={onSearchFilterSortPaginate}
          onSelectedKeysChange={onSelectedKeysChange}
          selectedKeys={selectedMaterials.map((x) => x.id)}
          totalRows={data?.total_count}
          pagination={pagination}
          sort={sort}
          search={search}
          filter={filter}
          columns={columns}
          rowLink={(record) => `${record.id}`}
        />
      </div>
    </div>
  );
}
